import { LeanbotAuth } from "./AuthService.js";
import { sleep } from "./helpers.js";

export class LeanbotCompiler {

  static #config = null;

  static setConfig(config) {
    LeanbotCompiler.#config = config;
  }

  #prevHash = "";
  #prevResponse = null;

  onCompileSucess = async () => {};
  onCompileError = async () => {};
  onCompileProgress = () => {};

  #compileStartMs = 0;
  #compileEndMs = 0;

  constructor(){
    if (!LeanbotCompiler.#config) {
      throw new Error("Missing LeanbotCompiler #config");
    } 

    // recover last response and hash from local storage (if any)
    this.#prevHash = localStorage.getItem(LeanbotCompiler.#config.LS_KEY_LAST_PREV_HASH) ?? "";
    const lastResponse = localStorage.getItem(LeanbotCompiler.#config.LS_KEY_LAST_PREV_RESPONSE);
    this.#prevResponse = lastResponse ? JSON.parse(lastResponse) : null;
    
    // saved lash prev hash before web refresh
    window.addEventListener("beforeunload", this.#SaveBeforeUnloadHandler);
  }

  async compile(sourceCode, compileServer = LeanbotCompiler.#config.Server) {
    const sketchName = "LeanbotSketch";

    const payload = {
      fqbn: LeanbotCompiler.#config.CompilePayload.fqbn,
      files: [
        {
          content: "#define USE_PRECOMPILED_ALL\n" + sourceCode,
          name: `${sketchName}/${sketchName}.ino`,
        },
      ],
      flags: LeanbotCompiler.#config.CompilePayload.flags,
      libs: LeanbotCompiler.#config.libs,
    };

    this.#compileStartMs = performance.now();
    this.#compileEndMs = 0;
    const predictedTotal = LeanbotCompiler.#config.PredictedTotalMs; //10000 ms = 10s

    const emitProgress = () => {
      const elapsedTime = (performance.now() - this.#compileStartMs);
      const estimatedTotal = Math.sqrt(elapsedTime ** 2 + predictedTotal ** 2);
      this.onCompileProgress(elapsedTime, estimatedTotal);
    };

    const progressTimer = setInterval(emitProgress, LeanbotCompiler.#config.ProgressEmitIntervalMs); // emit progress every 500ms

    try {
      // 2,000 load review (infra#983): was 4 tries 100 ms apart for ANY failure, so a busy compile server got 4x the
      // compiles exactly when it was overloaded, and a 504 (the server still compiling) started the same compile again.
      // Now: at most 2 retries, only when the server did not take the job (network error, 429, 502, 503), with
      // backoff ~1.5 s then ~3 s (jittered so a class does not retry in step).
      let attempt = 0;
      const  maxattempt = 2;
      let lastError = null;

      while(true){
        // attempt to send then fetch result from server
        try{
          const compileResult = await this.#requestCompile(payload, compileServer);

          this.#compileEndMs = performance.now();
          const elapsedTime = (this.#compileEndMs - this.#compileStartMs);

          this.onCompileProgress(elapsedTime, elapsedTime);
      
          if (compileResult.hex && compileResult.hex.trim() !== "") {

            // console.log("=== Log before removing precompiled lines ===");
            // console.log(compileResult.log);

            compileResult.log = compileResult.log.replace(
                /^.*precompiled.*(?:\r?\n|$)/gm,
                ""
            ); // Remove lines containing "precompiled" from compileResult.log

            // console.log("=== Log after removing precompiled lines ===");
            // console.log(compileResult.log);

            await this.onCompileSucess(compileResult.log);
            const hexText = this.#base64ToText(compileResult.hex);
            return {success: true, hexText: hexText, log: compileResult.log};
          } else {
            await this.onCompileError("compile_codeErr", compileResult.log);
            return {success: false, hexText: null, log: null};
          }

          // return compileResult;

        } catch (err){
          const message = err.message || String(err);
          lastError = message;
          console.log(`Catch Compile Error: ${message} at attempt ${attempt}`);

          
          if (!err.retryable || attempt++ >= maxattempt)
            break;

          await sleep(1500 * 2 ** (attempt - 1) * (0.5 + Math.random()));
        }
      }

      // all attempt failed => stop compile progress at once
      this.#compileEndMs = performance.now();
      this.onCompileProgress(1, 1);
      await this.onCompileError("compile_err", lastError);
      return {success: false, hexText: null, log: null};

    }finally{
      clearInterval(progressTimer); // clean interval whenever fail or success
    }
  }

  async #requestCompile(payload, compileServer) {
    const data = new TextEncoder().encode(JSON.stringify({ payload, compileServer }));
    const hashBuffer = await window.crypto.subtle.digest("SHA-256", data); // Returns a array buffer

    const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
    const payloadHash = hashArray.map((b) => b.toString(16).padStart(2, "0")).join(""); // convert bytes to hex string

    if (payloadHash === this.#prevHash) {
      await sleep(500); // simulate network delay
      return this.#prevResponse;
    }

    // client-side limit below the edge's 3 min read timeout
    const TIMEOUT_MS = 150 * 1000;
    const ctrl = typeof AbortController === "function" ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), TIMEOUT_MS) : null;
    let res;
    try {
      res = await fetch(`https://${compileServer}/v3/compile`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...LeanbotAuth.getAuthHeaders(),
        },
        body: JSON.stringify(payload),
        signal: ctrl ? ctrl.signal : undefined,
      });
    } catch (err) {
      if (timer) clearTimeout(timer);
      const timedOut = err && err.name === "AbortError";
      const e = new Error(timedOut
        ? `Compile server busy: no answer after ${TIMEOUT_MS / 1000} s. Wait 10-20 s and press Compile again.`
        : `Compile network error: ${err && err.message ? err.message : err}`);
      e.retryable = !timedOut; // a timed-out compile may still be running on the server
      throw e;
    }
    if (timer) clearTimeout(timer);

    if (!res.ok) {
      const busy = res.status === 429 || res.status >= 500;
      const e = new Error(busy
        ? `Compile server busy (HTTP ${res.status}). Wait 10-20 s and press Compile again.`
        : `Compile HTTP ${res.status}`);
      e.retryable = res.status === 429 || res.status === 502 || res.status === 503;
      throw e;
    }

    const result = await res.json(); // { hex, log }
    
    // CHỈ cache khi compile thành công
    if (result.hex && result.hex.trim() !== "") {
      this.#prevHash = payloadHash;
      this.#prevResponse = result;
    } else {
      // compile error → reset cache
      this.#prevHash = "";
      this.#prevResponse = null;
    }

    return result;
  }

  #SaveBeforeUnloadHandler = (event) => {
    // ref: https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
    // Recommended
    // event.preventDefault();
    if(this.#prevHash !== localStorage.getItem(LeanbotCompiler.#config.LS_KEY_LAST_PREV_HASH)){
      console.log("Saving last prev Hash to localStorage:", this.#prevHash);
      localStorage.setItem(LeanbotCompiler.#config.LS_KEY_LAST_PREV_HASH, this.#prevHash);
      localStorage.setItem(LeanbotCompiler.#config.LS_KEY_LAST_PREV_RESPONSE, JSON.stringify(this.#prevResponse)); // response can be null -> need json stringify
    }
    // Included for legacy support, e.g. Chrome/Edge < 119
    // event.returnValue = true;
  }

  #base64ToText(b64) {
    const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    return new TextDecoder("utf-8").decode(bytes);
  }

  elapsedTimeMs() {
    if (this.#compileEndMs === 0) // if compile not done yet
      return performance.now() - this.#compileStartMs;
    return this.#compileEndMs - this.#compileStartMs;
  }
}